import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, MapPin, Lock, Settings, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MyOrdersContent, MyAddressesContent, ChangePasswordContent, EditProfileContent } from './AccountContent';

const AccountSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('main');

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/login');
  };

  const menuItems = [
    { id: 'orders', icon: Package, label: 'My Orders' },
    { id: 'addresses', icon: MapPin, label: 'My Addresses' },
    { id: 'password', icon: Lock, label: 'Change Password' },
    { id: 'profile', icon: Settings, label: 'Edit Profile' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'orders': return <MyOrdersContent onBack={() => setActiveSection('main')} />;
      case 'addresses': return <MyAddressesContent onBack={() => setActiveSection('main')} />;
      case 'password': return <ChangePasswordContent onBack={() => setActiveSection('main')} />;
      case 'profile': return <EditProfileContent onBack={() => setActiveSection('main')} />;
      default: return renderMainMenu();
    }
  };

  const renderMainMenu = () => (
    <>
      {/* Professional Minimalist Header Profile Block */}
      <div className="p-3 flex items-center justify-between border-b border-slate-200 bg-slate-900 text-white shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-xs shrink-0">
            <span className="text-base font-bold text-white">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>
          <div className="min-w-0">
            <h2 className="text-xs font-bold tracking-wide truncate leading-tight">{user?.name || 'User Profile'}</h2>
            <p className="text-[10px] text-slate-400 font-medium truncate mt-0.5">{user?.email}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors active:scale-95">
          <X size={15} />
        </button>
      </div>

      {/* Main Settings Body Container Layout */}
      <div className="p-3 flex-1 space-y-1.5 overflow-y-auto bg-slate-50">
        <p className="px-1 text-[9px] font-bold text-slate-400 tracking-wider uppercase mb-1">Account Options</p>
        
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            className="w-full flex items-center justify-between p-2.5 bg-white border border-slate-200/60 hover:border-indigo-200 hover:bg-indigo-50/40 rounded-xl transition-all duration-150 group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-100 transition-colors shrink-0">
                <item.icon size={15} className="text-slate-600 group-hover:text-indigo-600 transition-colors" />
              </div>
              <span className="text-xs font-semibold text-slate-700 group-hover:text-slate-900 tracking-wide transition-colors truncate">
                {item.label}
              </span>
            </div>
            <ChevronRight size={14} className="text-slate-400 group-hover:text-indigo-600 transform group-hover:translate-x-0.5 transition-all" />
          </button>
        ))}

        {/* Action Logout Block */}
        <div className="pt-3 border-t border-slate-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-2.5 bg-red-50/50 border border-red-200 hover:bg-red-50 hover:border-red-300 rounded-xl transition-all group active:scale-[0.99]"
          >
            <div className="w-8 h-8 bg-white border border-red-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-red-200 transition-colors">
              <LogOut size={15} className="text-red-500" />
            </div>
            <span className="text-xs font-bold text-red-600 tracking-wide">Logout Account</span>
          </button>
        </div>
      </div>

      {/* Footer Info Block */}
      <div className="p-2.5 bg-white border-t border-slate-200 text-center">
        <p className="text-[9px] font-semibold text-slate-400 tracking-wider uppercase">
          Welcome to ProNest
        </p>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Background Overlay Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 transition-opacity cursor-pointer" 
        onClick={onClose} 
      />
      
      {/* Right Drawer Panel Container */}
      <div className="fixed right-0 top-0 h-full w-[70%] md:w-[50%] lg:w-[35%] xl:w-[24%] bg-white shadow-xl z-50 overflow-hidden flex flex-col border-l border-slate-200 animate-fade-slide">
        {renderContent()}
      </div>
    </>
  );
};

export default AccountSidebar;